/**
 * IGRA-110 — обёртка ручек пула `/vpn/pool*` (§8 ТЗ «Поделиться подпиской»).
 * Устроена как `circleApi.ts`/`devicesApi.ts`: при `VITE_IGRA_USE_MOCKS=1`
 * читает фикстуры §8, иначе идёт в бэкенд (ручки пишутся параллельно,
 * IGRA-110.4).
 *
 * Мок держит состав пула в памяти: созданное приглашение появляется в списке,
 * отозванное исчезает, исключённый участник пропадает. Без этого «список
 * обновился без перезагрузки» на моках проверить нечем.
 *
 * Dev-переключатели состояния (консоль браузера, без пересборки):
 *   localStorage.setItem('igra_vpn_pool_mock_state', 'seats_full' | 'email_limit' | 'trial' | 'member' | 'disabled')
 *   localStorage.setItem('igra_vpn_pool_invite_mock_state', 'expired' | 'revoked' | 'accepted' | 'link')
 *   localStorage.setItem('igra_vpn_pool_accept_mock_error', 'VPN_POOL_SEATS_FULL')
 *   location.reload()
 */
import { httpClient } from '@/shared/api/httpClient'
import {
  isMocksEnabled,
  vpnPoolFixture,
  vpnPoolSeatsFullFixture,
  vpnPoolEmailLimitFixture,
  vpnPoolTrialFixture,
  vpnPoolMemberFixture,
  vpnPoolDisabledFixture,
  vpnPoolInviteCreatedFixture,
  vpnPoolAcceptFixture,
  vpnPublicPoolInviteFixture,
  vpnPublicPoolInviteAcceptedFixture,
  vpnPublicPoolInviteExpiredFixture,
  vpnPublicPoolInviteLinkFixture,
  vpnPublicPoolInviteRevokedFixture,
} from '@/shared/api/mocks/vpnFixtures'
import type { VpnPaymentProvider } from '../types/billing'
import type {
  VpnPoolAcceptResponse,
  VpnPoolDevicePurchaseResponse,
  VpnPoolInviteResponse,
  VpnPoolLeaveResponse,
  VpnPoolPendingInvite,
  VpnPoolResponse,
  VpnPoolSuccessResponse,
  VpnPublicPoolInviteResponse,
} from '../types/pool'

const MOCK_STATE_KEY = 'igra_vpn_pool_mock_state'
const MOCK_INVITE_STATE_KEY = 'igra_vpn_pool_invite_mock_state'
const MOCK_ACCEPT_ERROR_KEY = 'igra_vpn_pool_accept_mock_error'

/** Коды ошибок пула из §8. Экран переводит их в свои строки. */
export const VPN_POOL_TRIAL_ONLY = 'VPN_POOL_TRIAL_ONLY'
export const VPN_POOL_NOT_OWNER = 'VPN_POOL_NOT_OWNER'
export const VPN_POOL_SEATS_FULL = 'VPN_POOL_SEATS_FULL'
export const VPN_POOL_NO_FREE_DEVICE = 'VPN_POOL_NO_FREE_DEVICE'
export const VPN_POOL_INVITE_EXISTS = 'VPN_POOL_INVITE_EXISTS'
export const VPN_POOL_EMAIL_IS_MEMBER = 'VPN_POOL_EMAIL_IS_MEMBER'
export const VPN_POOL_EMAIL_INVALID = 'VPN_POOL_EMAIL_INVALID'
export const VPN_POOL_EMAIL_LIMIT = 'VPN_POOL_EMAIL_LIMIT'
export const VPN_POOL_INVITE_NOT_FOUND = 'VPN_POOL_INVITE_NOT_FOUND'
export const VPN_POOL_MEMBER_NOT_FOUND = 'VPN_POOL_MEMBER_NOT_FOUND'

// Состав пула в памяти мока: приглашения сверх фикстуры, отозванные id,
// исключённые участники.
let mockExtraInvites: VpnPoolPendingInvite[] = []
let mockRevokedInviteIds: number[] = []
let mockKickedUserIds: number[] = []
let mockInviteSeq = 100

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Тело ошибки в форме `SpaceException`, как у `circleApi.ts`. `extra` — поля,
 * которые §8 кладёт рядом с кодом (у `VPN_POOL_HAS_OWN_SUBSCRIPTION` это
 * `paid_until` чужой подписки).
 */
function poolError(code: string, extra: Record<string, unknown> = {}): Error {
  const error = new Error(code) as Error & { response: { data: unknown } }
  error.response = { data: { result: 'error', error_message: code, ...extra } }
  return error
}

function mockBase(): VpnPoolResponse {
  const state = localStorage.getItem(MOCK_STATE_KEY)
  if (state === 'seats_full') return vpnPoolSeatsFullFixture as VpnPoolResponse
  if (state === 'email_limit') return vpnPoolEmailLimitFixture as VpnPoolResponse
  if (state === 'trial') return vpnPoolTrialFixture as VpnPoolResponse
  if (state === 'member') return vpnPoolMemberFixture as VpnPoolResponse
  if (state === 'disabled') return vpnPoolDisabledFixture as VpnPoolResponse
  return vpnPoolFixture as VpnPoolResponse
}

/** Состояние токена приглашения на моках: четыре статуса §8 плюс ссылка. */
function mockInvite(): VpnPublicPoolInviteResponse {
  const state = localStorage.getItem(MOCK_INVITE_STATE_KEY)
  if (state === 'expired') return vpnPublicPoolInviteExpiredFixture
  if (state === 'revoked') return vpnPublicPoolInviteRevokedFixture
  if (state === 'accepted') return vpnPublicPoolInviteAcceptedFixture
  if (state === 'link') return vpnPublicPoolInviteLinkFixture

  return vpnPublicPoolInviteFixture
}

function mockPool(): VpnPoolResponse {
  const base = mockBase()
  if (base.role === 'none') return base

  const invites = [...(base.invites ?? []), ...mockExtraInvites].filter(
    (invite) => !mockRevokedInviteIds.includes(invite.id),
  )
  const members = (base.members ?? []).filter((member) => !mockKickedUserIds.includes(member.user_id))

  return { ...base, invites, members }
}

export async function getPool(): Promise<VpnPoolResponse> {
  if (isMocksEnabled()) {
    await delay(300)
    return mockPool()
  }

  const response = await httpClient.get<{ data: VpnPoolResponse }>('/vpn/pool')
  return response.data
}

/**
 * `email` не задан — приглашение ссылкой: письмо не уходит и в суточный лимит
 * пяти писем (R5a) такое приглашение не входит.
 */
export async function invite(email?: string): Promise<VpnPoolInviteResponse> {
  if (isMocksEnabled()) {
    await delay(400)
    const pool = mockPool()
    if (pool.invite_blocked_reason === 'seats_full') throw poolError(VPN_POOL_SEATS_FULL)
    // Причины две, отказ ручки один (§8): «нет подписки» и «пробный» бэкенд
    // одинаково заворачивает VPN_POOL_TRIAL_ONLY.
    if (pool.invite_blocked_reason === 'trial_only' || pool.invite_blocked_reason === 'no_access') {
      throw poolError(VPN_POOL_TRIAL_ONLY)
    }
    if (email && pool.invite_blocked_reason === 'email_limit') throw poolError(VPN_POOL_EMAIL_LIMIT)

    const id = ++mockInviteSeq
    const created = {
      id,
      link: `${vpnPoolInviteCreatedFixture.invite.link}${id}`,
      expires_at: vpnPoolInviteCreatedFixture.invite.expires_at,
    }
    mockExtraInvites = [
      ...mockExtraInvites,
      {
        id,
        email: email ?? null,
        channel: email ? 'email' : 'link',
        expires_at: created.expires_at,
        // Письмо уходит только адресному приглашению — так же, как считает
        // факт отправки бэкенд (`email_sent_at !== null`).
        email_sent: Boolean(email),
        link: created.link,
      },
    ]

    return { invite: created, email_sent: Boolean(email) }
  }

  const response = await httpClient.post<{ data: VpnPoolInviteResponse }>(
    '/vpn/pool/invite',
    email ? { email } : {},
  )
  return response.data
}

export async function revokeInvite(id: number): Promise<VpnPoolSuccessResponse> {
  if (isMocksEnabled()) {
    await delay(300)
    const exists = (mockPool().invites ?? []).some((invite) => invite.id === id)
    if (!exists) throw poolError(VPN_POOL_INVITE_NOT_FOUND)
    mockRevokedInviteIds = [...mockRevokedInviteIds, id]
    return { success: true }
  }

  const response = await httpClient.post<{ data: VpnPoolSuccessResponse }>('/vpn/pool/invite/revoke', { id })
  return response.data
}

export async function kick(userId: number): Promise<VpnPoolSuccessResponse> {
  if (isMocksEnabled()) {
    await delay(300)
    const exists = (mockPool().members ?? []).some(
      (member) => member.user_id === userId && !member.is_owner,
    )
    if (!exists) throw poolError(VPN_POOL_MEMBER_NOT_FOUND)
    mockKickedUserIds = [...mockKickedUserIds, userId]
    return { success: true }
  }

  const response = await httpClient.post<{ data: VpnPoolSuccessResponse }>('/vpn/pool/kick', {
    user_id: userId,
  })
  return response.data
}

export async function accept(token: string): Promise<VpnPoolAcceptResponse> {
  if (isMocksEnabled()) {
    await delay(400)
    // Отказ приёма на моках проверяется тем же переключателем, что и остальные
    // состояния: без него семь кодов §8 на экране не увидеть вовсе.
    const forcedError = localStorage.getItem(MOCK_ACCEPT_ERROR_KEY)
    if (forcedError) {
      throw poolError(
        forcedError,
        forcedError === 'VPN_POOL_HAS_OWN_SUBSCRIPTION'
          ? { paid_until: vpnPoolFixture.paid_until }
          : {},
      )
    }

    return vpnPoolAcceptFixture
  }

  const response = await httpClient.post<{ data: VpnPoolAcceptResponse }>('/vpn/pool/accept', { token })
  return response.data
}

export async function leave(): Promise<VpnPoolLeaveResponse> {
  if (isMocksEnabled()) {
    await delay(400)
    return { success: true, buy_own_url: '/subscription?circle=5' }
  }

  const response = await httpClient.post<{ data: VpnPoolLeaveResponse }>('/vpn/pool/leave', {})
  return response.data
}

/**
 * Докупка ЛИЧНОГО устройства участником (R16). Сумма не передаётся: её
 * считает бэкенд тем же кодом, что построил `me.personal_offer` — инвариант
 * «объяснено = списано».
 */
export async function purchaseMemberDevice(
  extra: number,
  provider?: VpnPaymentProvider,
): Promise<VpnPoolDevicePurchaseResponse> {
  if (isMocksEnabled()) {
    await delay(400)
    // Мок отдаёт ТУ ЖЕ тройку, что бэкенд: без `payment_id` окно оплаты на
    // моках вело бы себя иначе, чем в проде, и как раз это расхождение и
    // спрятало бы висящий чекаут.
    return {
      payment_id: 990001,
      payment_url: 'https://pay.igra.now/mock/pool-device',
      amount: vpnPoolMemberFixture.me?.personal_offer?.charge_now ?? 140,
    }
  }

  const response = await httpClient.post<{ data: VpnPoolDevicePurchaseResponse }>(
    '/vpn/pool/devices/purchase',
    provider ? { extra, provider } : { extra },
  )
  return response.data
}

/**
 * Публичная ручка страницы приглашения — без авторизации.
 *
 * Тем же `httpClient`, что и остальная витрина для гостя
 * (`GET /vpn/public/checkout/options` в `publicCheckoutApi.ts`): страница
 * `/invite/:token` живёт в этой же SPA и на этом же домене, а перехватчик 401
 * на публичной ручке не срабатывает — она 401 не отдаёт. Отдельный `fetch`
 * заведён только в `modules/public` и по другой причине: те страницы работают
 * на key.igra.click вообще без аккаунта.
 *
 * Путь БЕЗ префикса `/api`: его добавляет `baseURL` клиента (`getApiBaseUrl`),
 * и второй такой префикс превращал адрес в `/api/api/…` — то есть в 404.
 */
export async function getInvite(token: string): Promise<VpnPublicPoolInviteResponse> {
  if (isMocksEnabled()) {
    await delay(300)
    return mockInvite()
  }

  const response = await httpClient.get<{ data: VpnPublicPoolInviteResponse }>(
    `/vpn/public/pool/invite/${encodeURIComponent(token)}`,
  )
  return response.data
}

/** Только для тестов: сбросить состав пула, накопленный моком. */
export function __resetPoolMockState(): void {
  mockExtraInvites = []
  mockRevokedInviteIds = []
  mockKickedUserIds = []
  mockInviteSeq = 100
}
